import { NavLink, Outlet, useNavigate, useLocation, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useBrand } from "../../contexts/BrandContext";
import { BrandLogoCircle } from "../../components/BrandLogoCircle";
import { NotificationBell } from "../../components/NotificationBell";
import { http } from "../../lib/api";
import { usePolling } from "../../lib/usePolling";
import { typesForPath } from "../../lib/sections";
import { LayoutDashboard, UserCog, Users, FileSignature, Wallet, LogOut, Briefcase, BookOpen, FileText, FolderKanban, Receipt, PlusCircle, SlidersHorizontal, Menu, X } from "lucide-react";

const Badge = ({ n }) => (n > 0 ? <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full bg-rose-600 text-white">{n}</span> : null);

export default function AgentLayout() {
  const { user, logout } = useAuth();
  const { brand } = useBrand();
  const nav = useNavigate();
  const [badges, setBadges] = useState({});

  const fetchBadges = async () => {
    if (user?.role !== "AGENT") return;
    try { const { data } = await http.get("/crm/me/badges"); setBadges(data || {}); } catch { /* noop */ }
  };
  useEffect(() => { fetchBadges(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [user]);
  usePolling(fetchBadges, { interval: 10000, enabled: user?.role === "AGENT" });
  useEffect(() => {
    const h = () => { fetchBadges(); };
    window.addEventListener("glowbit:badges-refresh", h);
    return () => window.removeEventListener("glowbit:badges-refresh", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loc = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => { setMenuOpen(false); window.scrollTo(0, 0); }, [loc.pathname]);
  useEffect(() => {
    if (user?.role !== "AGENT") return;
    const types = typesForPath(loc.pathname);
    if (!types) return;
    http.post(`/crm/me/notifications/read-by-type?types=${encodeURIComponent(types.join(","))}`)
      .then(() => { fetchBadges(); window.dispatchEvent(new Event("glowbit:notif-refresh")); })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.pathname, user]);
  const hasBadges = (badges.extra || 0) + (badges.materiali || 0) + (badges.documenti || 0) > 0;

  if (user === null) return null;
  if (!user) return <Navigate to="/platform/login" replace state={{ from: loc.pathname + loc.search }} />;
  if (user.role !== "AGENT") return <Navigate to="/platform/login" replace />;

  const doLogout = async () => { try { await logout(); } catch {} nav("/platform/login", { replace: true }); };

  const menuItem = "flex items-center gap-3 px-4 py-3 text-sm hover:bg-white/5 border-l-2 transition-colors";
  const linkFn = ({ isActive }) => `${menuItem} ${isActive ? "bg-white/10" : "border-transparent"}`;
  const activeStyle = ({ isActive }) => (isActive ? { borderLeftColor: "var(--brand-accent)" } : {});
  const close = () => setMenuOpen(false);

  return (
    <div data-testid="agent-shell" className="min-h-screen flex flex-col md:flex-row" style={{ background: "var(--brand-dark)", color: "white" }}>
      <aside className="w-full md:w-60 border-b md:border-b-0 md:border-r border-white/10 flex flex-col md:min-h-screen" style={{ background: "rgba(0,0,0,0.2)" }}>
        <div className="px-4 py-4 md:py-5 border-b border-white/10 flex items-center gap-3">
          <BrandLogoCircle className="h-10 w-10 md:h-11 md:w-11 shrink-0" imgTestId="agent-brand-logo" />
          <div className="min-w-0">
            <div className="text-sm font-semibold leading-tight truncate">{brand.display_name}</div>
            <div className="text-[10px] text-white/40">Portale agente</div>
          </div>
          <button type="button" onClick={() => setMenuOpen((v) => !v)} data-testid="agent-menu-toggle"
            className="ml-auto md:hidden relative p-2 rounded-md hover:bg-white/10" aria-label="Menu" aria-expanded={menuOpen}>
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            {!menuOpen && hasBadges && <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-rose-500" />}
          </button>
        </div>
        <div className={`${menuOpen ? "flex" : "hidden"} md:flex flex-col md:flex-1 min-h-0`} data-testid="agent-menu-panel">
          <nav className="py-2 md:py-4 md:flex-1 overflow-y-auto max-h-[70vh] md:max-h-none">
            <NavLink to="/agent/dashboard" onClick={close} className={linkFn} style={activeStyle} data-testid="anav-dashboard"><LayoutDashboard className="w-4 h-4" /> Dashboard</NavLink>
            <NavLink to="/agent/profile" onClick={close} className={linkFn} style={activeStyle} data-testid="anav-profile"><UserCog className="w-4 h-4" /> Profilo</NavLink>
            <NavLink to="/agent/agreement" onClick={close} className={linkFn} style={activeStyle} data-testid="anav-agreement"><FileSignature className="w-4 h-4" /> Accordo</NavLink>
            <NavLink to="/agent/contacts" onClick={close} className={linkFn} style={activeStyle} data-testid="anav-contacts"><Users className="w-4 h-4" /> Contatti</NavLink>
            <NavLink to="/agent/deals" onClick={close} className={linkFn} style={activeStyle} data-testid="anav-deals"><Briefcase className="w-4 h-4" /> Trattative</NavLink>
            <NavLink to="/agent/offerte" onClick={close} className={linkFn} style={activeStyle} data-testid="anav-offerte"><FileText className="w-4 h-4" /> Offerte</NavLink>
            <NavLink to="/agent/progetti" onClick={close} className={linkFn} style={activeStyle} data-testid="anav-progetti"><FolderKanban className="w-4 h-4" /> Progetti</NavLink>
            <NavLink to="/agent/extra" onClick={close} className={linkFn} style={activeStyle} data-testid="anav-extra"><PlusCircle className="w-4 h-4" /> Extra <Badge n={badges.extra} /></NavLink>
            <NavLink to="/agent/modalita" onClick={close} className={linkFn} style={activeStyle} data-testid="anav-modalita"><SlidersHorizontal className="w-4 h-4" /> Modalità</NavLink>
            <NavLink to="/agent/materials" onClick={close} className={linkFn} style={activeStyle} data-testid="anav-materials"><BookOpen className="w-4 h-4" /> Materiali <Badge n={badges.materiali} /></NavLink>
            <NavLink to="/agent/pagamenti" onClick={close} className={linkFn} style={activeStyle} data-testid="anav-pagamenti"><Wallet className="w-4 h-4" /> Pagamenti</NavLink>
            <NavLink to="/agent/documenti" onClick={close} className={linkFn} style={activeStyle} data-testid="anav-documenti"><FileSignature className="w-4 h-4" /> Documenti <Badge n={badges.documenti} /></NavLink>
            <NavLink to="/agent/commissions" onClick={close} className={linkFn} style={activeStyle} data-testid="anav-commissions"><Wallet className="w-4 h-4" /> Provvigioni</NavLink>
            <NavLink to="/agent/prospetti" onClick={close} className={linkFn} style={activeStyle} data-testid="anav-prospetti"><Receipt className="w-4 h-4" /> Prospetti/Fattura</NavLink>
          </nav>
          <div className="border-t border-white/10 p-4">
            <div className="text-xs mb-3 break-all" data-testid="agent-user-email">{user.email}</div>
            <button onClick={doLogout} data-testid="agent-logout" className="w-full text-left text-[11px] py-1 text-white/60 hover:text-white/90 flex items-center gap-2"><LogOut className="w-3 h-3" /> Esci</button>
          </div>
        </div>
      </aside>
      <main className="flex-1 min-h-screen overflow-x-hidden" style={{ background: "var(--bg-main)", color: "var(--text-primary)" }}>
        <header className="border-b px-6 py-4 flex items-center gap-3" style={{ borderColor: "var(--border-silver)", background: "var(--bg-card)" }}>
          <span className="font-serif text-xl">Portale agente</span>
          <div className="ml-auto"><NotificationBell tone="light" /></div>
        </header>
        <div className="p-3 md:p-6"><Outlet /></div>
      </main>
    </div>
  );
}
